import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import { RootState, useAppDispatch } from "store";
import { fetchTrivia } from "store/actions/trivia";

const ANSWER_DURATION = 2;
const ANSWER_SCREEN_DURATION = 7;
const DELAY_BEFORE_BANNER = 1;
const INTRO_SCREEN_DURATION = 3;
const MIN_BANNER_DISPLAY_DURATION = 3;
const QUESTION_SCREEN_DURATION = 7;

export const useTrivia = () => {
  const dispatch = useAppDispatch();

  const questionSlugs = useSelector((state: RootState) => state.trivia.slugs);
  const questions = useSelector((state: RootState) => state.trivia.questions);

  const availableSlugs = useRef([...questionSlugs]);
  const questionNumber = useRef(0);

  const nextQuestion = () => {
    if (availableSlugs.current.length === 0) {
      availableSlugs.current = [...questionSlugs];
      questionNumber.current = 0;

      if (availableSlugs.current.length === 0) {
        return undefined;
      }
    }

    const nextSlug = availableSlugs.current[0];
    const nextQuestionNumber = questionNumber.current + 1;

    availableSlugs.current.shift();
    questionNumber.current = nextQuestionNumber;

    return {
      question: questions[nextSlug],
      questionNumber: nextQuestionNumber,
    };
  };

  const submitAnswer = (questionSlug: string, answerSlug: string) => {
    const answer = questions[questionSlug].answers.find(({ slug }) => slug === answerSlug);

    return answer?.correct;
  };

  useEffect(() => {
    if (!availableSlugs.current.length && questionSlugs.length) {
      availableSlugs.current = [...questionSlugs];
    }
  }, [questionSlugs]);

  useEffect(() => {
    if (!questionSlugs.length) {
      // INVESTIGATE: Should we call this ahead of time? Perhaps have a last grabbed value in reducer
      // to check for staleness so we can re-fetch if needed. Could either export the fetchQuestions
      // method or just call `useTrivia();` without caring about the return value to pre-fetch.
      dispatch(fetchTrivia());
    }
  }, [dispatch, questionSlugs]);

  return {
    nextQuestion,
    submitAnswer,
    answerDuration: ANSWER_DURATION,
    answerScreenDuration: ANSWER_SCREEN_DURATION,
    delayBeforeBanner: DELAY_BEFORE_BANNER,
    introScreenDuration: INTRO_SCREEN_DURATION,
    minBannerDisplayDuration: MIN_BANNER_DISPLAY_DURATION,
    questionScreenDuration: QUESTION_SCREEN_DURATION,
  };
};
